// Omnichannel reports — provider-neutral (built on the interaction ledger).
import { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard, Send, Timer, Bot, Route, Loader2, Download, FileSpreadsheet, Search, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MultiSelect, isoToday, isoDaysAgo } from "./_daterange";
import { CHANNEL_OPTIONS } from "./omni/_shared";
import OmniOverview from "./omni/OmniOverview";
import OmniChannels from "./omni/OmniChannels";
import OmniResponse from "./omni/OmniResponse";
import OmniAi from "./omni/OmniAi";
import OmniJourneys from "./omni/OmniJourneys";

const TABS = [
  { id: "overview", label: "Overview", Icon: LayoutDashboard, C: OmniOverview },
  { id: "channels", label: "Channel Delivery", Icon: Send, C: OmniChannels },
  { id: "response", label: "Response & Resolution", Icon: Timer, C: OmniResponse },
  { id: "ai", label: "AI vs Agent", Icon: Bot, C: OmniAi },
  { id: "paths", label: "Customer Journeys", Icon: Route, C: OmniJourneys },
];

export default function OmniReportsPage() {
  const [tab, setTab] = useState("overview");
  const [from, setFrom] = useState(isoDaysAgo(6));
  const [to, setTo] = useState(isoToday());
  const [channels, setChannels] = useState([]);
  const [sla, setSla] = useState(300);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState("");
  const [rebuilding, setRebuilding] = useState(false);

  const params = useMemo(() => {
    const p = { date_from: from, date_to: to };
    if (channels.length) p.channels = channels.join(",");
    if (tab === "response") p.sla_seconds = sla;
    return p;
  }, [from, to, channels, tab, sla]);

  async function run() {
    setBusy(true);
    try { setData((await api.get(`/omni-reports/${tab}`, { params })).data); }
    catch (e) { toast.error(formatErr(e)); setData(null); }
    finally { setBusy(false); }
  }
  useEffect(() => { setData(null); run(); /* eslint-disable-next-line */ }, [tab]);

  async function exportFile(fmt) {
    setDl(fmt);
    try {
      const r = await api.get(`/omni-reports/${tab}/export`, { params: { ...params, fmt }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `omni-${tab}-${from}_to_${to}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(formatErr(e)); } finally { setDl(""); }
  }

  async function rebuild() {
    setRebuilding(true);
    try {
      const r = await api.post("/omni-reports/rebuild");
      toast.success(`Re-synced ${r.data.conversations} conversations and ${r.data.calls} calls`);
      run();
    } catch (e) { toast.error(formatErr(e)); } finally { setRebuilding(false); }
  }

  const Active = TABS.find((t) => t.id === tab).C;
  return (
    <div className="px-4 sm:px-6 py-6" data-testid="omni-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Omnichannel Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every channel in one place — works the same whichever provider powers WhatsApp, email or social.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={rebuild} disabled={rebuilding} className="gap-1.5" data-testid="omni-rebuild">
            {rebuilding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-sync history
          </Button>
          <Button variant="outline" onClick={() => exportFile("csv")} disabled={!data || !!dl} className="gap-1.5" data-testid="omni-export-csv">
            {dl === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV
          </Button>
          <Button onClick={() => exportFile("xlsx")} disabled={!data || !!dl} className="gap-1.5" data-testid="omni-export-xlsx">
            {dl === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
          </Button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-border overflow-x-auto" data-testid="omni-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`omni-tab-${id}`}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap transition-colors
              ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <Card className="p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="omni-filters">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="omni-date-from" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">To</label>
          <input type="date" value={to} min={from} max={isoToday()} onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="omni-date-to" />
        </div>
        <MultiSelect label="Channel" options={CHANNEL_OPTIONS} selected={channels} onChange={setChannels} testid="omni-filter-channel" />
        {tab === "response" && (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground block mb-1">SLA (seconds)</label>
            <input type="number" min={10} value={sla} onChange={(e) => setSla(Number(e.target.value) || 300)}
              className="h-9 w-28 rounded-md border border-border bg-background px-2 text-sm" data-testid="omni-sla" />
          </div>
        )}
        <Button onClick={run} disabled={busy} className="gap-1.5 h-9" data-testid="omni-apply">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Apply
        </Button>
      </Card>

      {busy && !data && <div className="py-16 text-center text-muted-foreground" data-testid="omni-loading"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>}
      {data && <Active data={data} params={params} />}
    </div>
  );
}
